export const FAVORITE_WIDGET_SELECTOR = '.kl-hub-favorites-widget';

const HYDRATE_API_POLL_MAX = 120;

export class KlaviyoFavorites {
  constructor() {
    this.hydrateGeneration = 0;
    this.activeProductRequest = 0;
    this.cachedShopOrigin = '';
  }

  /**
   * Strips non-digit characters from Shopify product IDs.
   * @param {string|number|null|undefined} id
   * @returns {string}
   */
  normalizeId(id) {
    return id ? String(id).replace(/\D+/g, '') : '';
  }

  /**
   * @returns {boolean}
   */
  isGuestShopper() {
    return !window.isLoggedIn;
  }

  /**
   * Resolves the storefront origin for absolute product URLs.
   * Canonical link is preferred so preview/dev hosts still emit production URLs.
   * @returns {string}
   */
  getShopOrigin() {
    if (this.cachedShopOrigin) return this.cachedShopOrigin;

    const canonical = document.querySelector('link[rel="canonical"]')?.href;
    if (canonical) {
      try {
        this.cachedShopOrigin = new URL(canonical).origin;
      } catch (e) {
        this.cachedShopOrigin = '';
      }
      if (this.cachedShopOrigin) return this.cachedShopOrigin;
    }

    const shopUrl = window.customerAccount?.oauthOrigin || window.siteGlobals?.customerAccount?.oauthOrigin;
    if (shopUrl) {
      try {
        this.cachedShopOrigin = new URL(shopUrl).origin;
      } catch (e) {
        this.cachedShopOrigin = shopUrl.replace(/\/$/, '');
      }
      return this.cachedShopOrigin;
    }

    if (window.domain) {
      this.cachedShopOrigin = `https://${String(window.domain).replace(/^https?:\/\//, '')}`;
      return this.cachedShopOrigin;
    }

    this.cachedShopOrigin = window.location.origin;
    return this.cachedShopOrigin;
  }

  /**
   * @param {string} url
   * @returns {string}
   */
  toAbsoluteUrl(url) {
    if (!url) return '';
    if (/^https?:\/\//i.test(url)) return url;
    if (url.startsWith('//')) return `${window.location.protocol}${url}`;
    if (url.startsWith('/')) return `${this.getShopOrigin()}${url}`;
    return url;
  }

  /**
   * @param {string} handle
   * @returns {string}
   */
  buildProductUrl(handle) {
    return handle ? `${this.getShopOrigin()}/products/${handle}` : '';
  }

  /**
   * @param {string|Array<{node?: {url?: string, image?: {url?: string}}, url?: string}>} imageOrImages
   * @returns {string}
   */
  resolveFeaturedImageUrl(imageOrImages) {
    if (!imageOrImages) return '';
    if (typeof imageOrImages === 'string') return this.toAbsoluteUrl(imageOrImages);

    if (!Array.isArray(imageOrImages) || !imageOrImages.length) return '';

    const first = imageOrImages[0];
    return this.toAbsoluteUrl(first?.node?.url || first?.node?.image?.url || first?.url || '');
  }

  /**
   * @param {string} productId
   * @param {string} handle
   * @param {string} productUrl
   * @param {string} [name]
   * @param {string} [imageUrl]
   */
  trackViewedProduct(productId, handle, productUrl, name = '', imageUrl = '') {
    if (!productId) return;

    const item = {
      ProductID: productId,
      URL: productUrl || (handle ? this.buildProductUrl(handle) : window.location.href)
    };

    if (name) item.Name = name;
    if (imageUrl) item.ImageURL = this.toAbsoluteUrl(imageUrl);

    if (window.klaviyo?.track) window.klaviyo.track('Viewed Product', item);
    else if (window._learnq) window._learnq.push(['track', 'Viewed Product', item]);
  }

  /**
   * Sets customerHub.activeProduct for the selected product.
   * Previous product fields are not reused when the product ID changes.
   * @param {string} productId
   * @param {string} productUrl
   * @param {string} [imageUrl]
   * @param {string} [productName]
   */
  setActiveProduct(productId, productUrl, imageUrl = '', productName = '') {
    if (!window.customerHub || !productId) return;

    const previous = window.customerHub.activeProduct || {};
    const sameProduct = String(previous.id) === String(productId);

    window.customerHub.activeProduct = {
      id: productId,
      link: productUrl || (sameProduct ? previous.link : '') || '',
      name: productName || (sameProduct ? previous.name : '') || '',
      imageUrl: this.toAbsoluteUrl(imageUrl || (sameProduct ? previous.imageUrl : '') || ''),
      variants: sameProduct ? previous.variants || [] : []
    };
  }

  /**
   * Invokes Klaviyo Customer Hub hydrateProduct, polling briefly if the API loads late.
   * @param {string} handle
   */
  invokeHydrateProduct(handle) {
    if (!handle) return;

    if (window.customerHubApi?.hydrateProduct) {
      window.customerHubApi.hydrateProduct(handle);
      return;
    }

    let attempts = 0;
    const tick = () => {
      if (window.customerHubApi?.hydrateProduct) {
        window.customerHubApi.hydrateProduct(handle);
        return;
      }
      if (++attempts < HYDRATE_API_POLL_MAX) requestAnimationFrame(tick);
    };

    tick();
  }

  /**
   * @returns {number}
   */
  nextHydrateGeneration() {
    return ++this.hydrateGeneration;
  }

  /**
   * @returns {number}
   */
  currentHydrateGeneration() {
    return this.hydrateGeneration;
  }

  /**
   * Hydrates customerHub.activeProduct for the selected handle.
   * Uses the passed image immediately, then Hub API or /products/{handle}.js for full data.
   * @param {string} productId
   * @param {string} handle
   * @param {number} generation
   * @param {string} [imageUrl]
   * @param {string} [productName]
   */
  hydrateActiveProductFromHandle(productId, handle, generation, imageUrl = '', productName = '') {
    if (!handle) return;

    const productUrl = this.buildProductUrl(handle);
    const resolvedImage = this.toAbsoluteUrl(imageUrl);

    this.setActiveProduct(productId, productUrl, resolvedImage, productName);

    if (window.customerHubApi?.hydrateProduct) {
      window.customerHubApi.hydrateProduct(handle);
      if (resolvedImage) return;
    }

    if (!window.customerHub) return;

    const requestId = ++this.activeProductRequest;

    fetch(`/products/${handle}.js`)
      .then(res => (res.ok ? res.json() : null))
      .then(product => {
        if (!product || generation !== this.hydrateGeneration || requestId !== this.activeProductRequest) return;

        window.customerHub.activeProduct = {
          id: String(product.id),
          link: this.buildProductUrl(product.handle),
          name: product.title || productName || '',
          imageUrl: this.toAbsoluteUrl(product.featured_image || product.images?.[0] || resolvedImage || ''),
          variants: this.orderSelectedVariantFirst(product.variants || [])
        };
      })
      .catch(error => {
        console.warn('[favorites] Failed to hydrate active product', error);
      });
  }

  /**
   * Puts the PDP-selected variant first so Klaviyo hydrates that size/color.
   * @param {Array} variants
   * @returns {Array}
   */
  orderSelectedVariantFirst(variants) {
    if (!Array.isArray(variants) || variants.length < 2) return variants || [];

    const selectedId = this.normalizeId(window.__pdpAddButtonPayload?.variantId);
    if (!selectedId) return variants;

    const idx = variants.findIndex(v => this.normalizeId(v?.id) === selectedId);
    if (idx < 1) return variants;

    const next = variants.slice();
    next.unshift(next.splice(idx, 1)[0]);
    return next;
  }

  /**
   * @param {HTMLElement} container
   * @returns {HTMLElement|null}
   */
  getFavoriteSlot(container) {
    return container.querySelector('.klaviyo-wishlist-slot, [data-product-id]');
  }

  /**
   * @param {string} productId
   * @param {string} productUrl
   * @returns {HTMLDivElement}
   */
  createWishlistSlot(productId, productUrl) {
    const slot = document.createElement('div');
    slot.className = 'klaviyo-wishlist-slot';
    slot.setAttribute('data-product-id', productId);
    if (productUrl) slot.setAttribute('data-product-url', productUrl);
    return slot;
  }

  /**
   * @returns {boolean}
   */
  isKlaviyoHubOpen() {
    return location.hash.indexOf('k-hub') !== -1 || !!document.querySelector('.kl-hub-drawer--opened, .kl-hub-modal-veil--opened');
  }

  /**
   * @returns {boolean}
   */
  isHubEnabled() {
    return !!(window.customerHub || window.customerHubApi || customElements.get('klaviyo-favorites-button') || document.querySelector('.kl-hub-launcher, [class*="HubLauncher-floatingWidget"]'));
  }

  /**
   * @param {string|number|null|undefined} productId
   * @returns {boolean}
   */
  isProductFavorited(productId) {
    const normalized = this.normalizeId(productId);
    if (!normalized || !window.customerHub) return false;

    const lists = [window.customerHub.favorites, window.customerHub.wishlist, window.customerHub.favoriteProducts, window.customerHub.items];
    return lists.some(list => Array.isArray(list) && list.some(item => this.normalizeId(item?.id || item?.productId || item?.ProductID) === normalized));
  }

  /**
   * @param {HTMLElement} container
   * @param {string|number|null|undefined} productId
   */
  syncWidgetFavoriteVisual(container, productId) {
    const icon = container.querySelector('.kl-hub-wishlist-icon');
    if (!icon) return;
    icon.classList.toggle('kl-hub-wishlist-icon--active', this.isProductFavorited(productId));
  }

  /**
   * Reads the favorite product context a surface stored on its container element.
   * @param {HTMLElement} container
   * @returns {{productId: string, handle: string, productUrl: string, imageUrl: string, productName: string}}
   */
  readFavoriteContext(container) {
    const productId = this.normalizeId(container.getAttribute('data-fav-pid'));
    const handle = container.getAttribute('data-fav-handle') || '';

    return {
      productId,
      handle,
      productUrl: container.getAttribute('data-fav-url') || this.buildProductUrl(handle),
      imageUrl: container.getAttribute('data-fav-image') || '',
      productName: container.getAttribute('data-fav-name') || ''
    };
  }
}

export const favorites = new KlaviyoFavorites();
